import React, { useState } from "react";
import { useTheme } from "next-themes";
import axios from "axios";
import { Smartphone, Bell, MessageCircle, FileText, CheckCircle2, Send, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";
import Reveal from "@/components/brams/Reveal";

const ACCENT = "#E11D2E";
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const FEATURES = [
  { icon: FileText, label: "Devis en 4 étapes", desc: "Décrivez votre besoin, joignez vos inspirations." },
  { icon: MessageCircle, label: "Messagerie temps réel", desc: "Échangez images, PDF et briefs directement." },
  { icon: CheckCircle2, label: "Suivi de projet", desc: "Timeline claire à chaque étape." },
  { icon: Bell, label: "Notifications instantanées", desc: "Restez informé où que vous soyez." },
];

export default function MobileApp() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section
      id="app-mobile"
      data-testid="mobile-app-section"
      className={cn("relative py-28 md:py-36", isDark ? "bg-black" : "bg-[#fafafa]")}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Application mobile"
          title={
            <>
              Votre studio Brams,{" "}
              <em className="not-italic opacity-60">dans votre poche</em>.
            </>
          }
          subtitle="Bientôt disponible sur l'App Store et Google Play. Suivez chaque projet, échangez avec le studio et validez vos maquettes depuis votre iPhone ou Android."
        />

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: features */}
          <Reveal>
            <div className="space-y-4">
              {FEATURES.map((f, i) => (
                <div
                  key={f.label}
                  className={cn(
                    "flex items-start gap-5 p-6 rounded-2xl transition-all duration-500 ease-out hover:-translate-y-1",
                    isDark
                      ? "bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.05]"
                      : "bg-white border border-black/[0.06] hover:border-black/[0.12]"
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center w-12 h-12 rounded-xl shrink-0",
                      isDark
                        ? "bg-black border border-white/10"
                        : "bg-black/[0.02] border border-black/[0.08]"
                    )}
                  >
                    <f.icon
                      size={18}
                      strokeWidth={1.5}
                      className={cn(isDark ? "text-white" : "text-black")}
                    />
                  </div>
                  <div>
                    <h3
                      className={cn(
                        "font-display font-semibold text-lg tracking-tight",
                        isDark ? "text-white" : "text-black"
                      )}
                    >
                      {f.label}
                    </h3>
                    <p
                      className={cn(
                        "font-body text-sm mt-1",
                        isDark ? "text-white/60" : "text-black/60"
                      )}
                    >
                      {f.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Right: CTA card */}
          <Reveal delay={200}>
            <div
              className={cn(
                "relative overflow-hidden rounded-3xl p-10 md:p-12",
                isDark
                  ? "bg-[#0A0A0A] border border-white/[0.10]"
                  : "bg-black text-white"
              )}
              style={{
                boxShadow: `0 30px 80px -40px ${ACCENT}55`,
              }}
            >
              <span
                className="absolute left-0 top-0 h-[2px] w-full"
                style={{ background: ACCENT }}
              />
              <div
                aria-hidden="true"
                className="absolute -inset-10 pointer-events-none"
                style={{
                  background: `radial-gradient(closest-side, ${ACCENT}22, transparent 65%)`,
                  filter: "blur(50px)",
                }}
              />
              <div className="relative">
                <div
                  className={cn(
                    "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.35em] mb-8",
                    "bg-white/[0.06] border border-white/[0.15] text-white"
                  )}
                >
                  <Smartphone size={11} style={{ color: ACCENT }} />
                  Bientôt disponible
                </div>

                <h3 className="font-display font-semibold text-3xl md:text-4xl tracking-tighter leading-tight text-white">
                  Espace client{" "}
                  <span style={{ color: ACCENT }}>premium.</span>
                </h3>

                <p className="mt-4 font-body text-sm md:text-[15px] leading-relaxed text-white/70 max-w-md">
                  L'application Brams Graphic sera prochainement disponible sur iOS et Android. Elle offrira à nos clients un espace pour suivre leurs projets, dialoguer avec le studio et valider chaque maquette.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <div
                    className={cn(
                      "flex items-center gap-3 px-5 py-3.5 rounded-2xl border",
                      "bg-white/[0.04] border-white/[0.12] cursor-not-allowed"
                    )}
                  >
                    <AppleGlyph />
                    <div className="text-left">
                      <div className="text-[9px] uppercase tracking-widest text-white/50">Bientôt sur</div>
                      <div className="font-display font-semibold text-sm text-white leading-tight">App Store</div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "flex items-center gap-3 px-5 py-3.5 rounded-2xl border",
                      "bg-white/[0.04] border-white/[0.12] cursor-not-allowed"
                    )}
                  >
                    <AndroidGlyph />
                    <div className="text-left">
                      <div className="text-[9px] uppercase tracking-widest text-white/50">Bientôt sur</div>
                      <div className="font-display font-semibold text-sm text-white leading-tight">Google Play</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <WaitlistForm />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("both");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const valid = /\S+@\S+\.\S+/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        platform,
        source: "web-landing",
      });
      setStatus("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur, réessayez.");
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="waitlist-success"
        className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.12]"
      >
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
          style={{ background: ACCENT }}
        >
          <Check size={14} strokeWidth={3} className="text-white" />
        </div>
        <div>
          <div className="font-display font-semibold text-sm text-white">
            Vous êtes sur la liste !
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-0.5">
            Nous vous préviendrons dès le lancement.
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/70 mb-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />
        Rejoindre la liste d'attente
      </div>

      <div className="flex items-center gap-1 rounded-full border border-white/[0.15] bg-white/[0.04] pl-4 pr-1 py-1">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.com"
          data-testid="waitlist-email"
          className="flex-1 bg-transparent outline-none text-sm font-body text-white placeholder-white/40 py-2"
        />
        <button
          type="submit"
          disabled={!valid || status === "sending"}
          data-testid="waitlist-submit"
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-display font-semibold transition-all",
            valid && status !== "sending"
              ? "bg-white text-black hover:scale-[1.02]"
              : "bg-white/20 text-white/40 cursor-not-allowed"
          )}
        >
          {status === "sending" ? "..." : "M'inscrire"}
          {status !== "sending" && <Send size={11} strokeWidth={2} />}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "ios", label: "iOS" },
          { id: "android", label: "Android" },
          { id: "both", label: "Les deux" },
        ].map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPlatform(p.id)}
            data-testid={`waitlist-platform-${p.id}`}
            className={cn(
              "px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-display transition-all border",
              platform === p.id
                ? "bg-white text-black border-white"
                : "bg-transparent text-white/60 border-white/15 hover:border-white/30 hover:text-white"
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-[11px]" style={{ color: ACCENT }} data-testid="waitlist-error">
          {error}
        </div>
      )}
    </form>
  );
}

function AppleGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.53 15.93c-.27.63-.6 1.23-.98 1.79-.52.75-.94 1.27-1.27 1.55-.5.44-1.04.66-1.62.68-.42 0-.92-.12-1.51-.36-.59-.24-1.13-.36-1.63-.36-.52 0-1.08.12-1.68.36-.6.24-1.08.37-1.44.38-.55.02-1.11-.21-1.66-.7-.36-.3-.79-.85-1.3-1.63C3.25 16.79 2.8 15.87 2.44 14.83c-.39-1.13-.58-2.23-.58-3.29 0-1.22.26-2.27.79-3.15.42-.71.97-1.27 1.67-1.68.7-.41 1.46-.62 2.28-.63.44 0 1.02.14 1.75.4.72.27 1.18.4 1.38.4.15 0 .66-.16 1.53-.46.83-.28 1.53-.4 2.1-.35 1.55.13 2.72.74 3.5 1.85-1.39.84-2.08 2.02-2.06 3.53.02 1.18.44 2.15 1.27 2.93.38.36.8.63 1.27.83-.1.3-.21.58-.31.85zM14.05 3.6c0 .91-.33 1.76-.99 2.55-.8.94-1.77 1.48-2.82 1.4-.01-.11-.02-.22-.02-.34 0-.87.38-1.81 1.05-2.58.34-.39.76-.72 1.28-.98.52-.26 1.01-.4 1.47-.42.01.12.03.24.03.37z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function AndroidGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.5 17.5V6.7c0-.35.13-.65.4-.9L11 13.5l-8.1 4.4a1.28 1.28 0 01-.4-.4zm.85-12.3l8.55 4.65 1.6-1.6-9.4-5.1c-.3-.15-.55-.15-.75.05zm10.2 6.55l2 2 3.6-2c.55-.3.55-.75 0-1.05l-3.6-2-2 3.05zm-1.6 1.6l-9.5 5.15c.2.2.45.2.75.05l9.4-5.1-.65-.1z"
        fill="#FFFFFF"
      />
    </svg>
  );
}
